import './Reel.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReelProps } from './Reel.interface';
import { Loader, ShareSheet, UnitName, UnitPhoto, Verified } from '$shared/components';
import { useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import {
  IcBan28,
  IcDollar28,
  IcLike24,
  IcMessage28,
  IcMore24,
  IcReport24,
  IcShare24,
  IcView20,
} from '$assets';
import { goTo } from '$navigation/helper';
import {
  CHAT,
  PROFILE,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
} from '$shared/constants/pages';
import { reelsActions } from '$store/reels';
import {
  classNames,
  confirmSheet,
  formatBigNumber,
  getStorageItem,
  statReachGoal,
} from '$utils';
import { AttachLink, AuthPopoverState, CurrencyEnum } from '$store/models';
import { mainActions, mainSelector } from '$store/main';
import { ActionBottomSheet, List, ListItem, LottieIcon } from '$uikit';
import { authIsLoggedSelector } from '$store/auth';
import { profileActions } from '$store/profile';
import router from '$navigation/router';
import { balanceActions } from '$store/balance';
import { followersSelector } from '$store/followers';
import { useCurrentUserIsTester } from '$hooks/useUserInfo';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { ReportReason } from '$shared/constants/reportReason';

export const Reel: FC<ReelProps> = (props) => {
  const {
    reel,
    isActive,
    isVisible,
    isSelected,
    isAnimating,
    isLoaded,
    index,
    isWatched,
    isPlay,
    isFollowed = false,
  } = props;
  const owner = useUserInfo(reel.ownerId);
  const user = useCurrentUserInfo();
  const t = useTranslator();
  const dispatch = useDispatch();
  const currentUserIsTester = useCurrentUserIsTester();
  const startViewAt = useRef(0);
  const isLogged = useSelector(authIsLoggedSelector);
  const { decryptKeys } = useSelector(mainSelector);
  const reelsSwiped = useRef(getStorageItem('reels_swiped') === '1');
  const [isOpenReelMenu, setOpenReelMenu] = useState(false);
  const { following } = useSelector(followersSelector);
  const isFollowFromMe = following[reel.ownerId];
  const isCurrentUserReel = owner.id === user.id;

  const [isReported, setReported] = useState(false);
  const moreReelOptions = useMemo(() => {
    const array: Array<ActionBottomSheetOption> = [];

    if (isReported) {
      // @ts-ignore
      array.push<any>(
        ...[
          {
            label: t('post_action_report_spam'),
            value: ReportReason.SPAM,
            mode: 'default',
            groupId: 'report',
          },
          {
            label: t('post_action_report_offensive'),
            value: ReportReason.FALSE_INFO,
            mode: 'default',
            groupId: 'report',
          },
          {
            label: t('post_action_report_false_info'),
            value: ReportReason.OFFENSIVE,
            mode: 'default',
            groupId: 'report',
          },
        ],
      );
    } else {
      if (!isCurrentUserReel && isFollowFromMe) {
        array.push({
          label: t('reel_action_unfollow'),
          value: 'unfollow',
          mode: 'default',
        });
      }

      if (currentUserIsTester || isCurrentUserReel) {
        if (reel.isDeleted) {
          array.push({
            label: t('reel_action_restore_clip'),
            value: 'restore',
            mode: 'default',
          });
        } else {
          array.push({
            label: t('clips_delete_title'),
            value: 'delete',
            mode: 'alert',
          });
        }
      }

      if (!isCurrentUserReel) {
        array.push({
          label: t('reel_action_report'),
          value: 'report',
          mode: 'alert',
        });
      }
    }

    return array;
  }, [reel, isCurrentUserReel, isFollowFromMe, currentUserIsTester, isReported]);

  useEffect(() => {
    if (isActive) {
      const startAt = Date.now();
      startViewAt.current = startAt;
      return () => {
        const duration = Date.now() - startAt;
        if (isLogged) {
          dispatch(
            reelsActions.sendView({
              reelId: reel.id,
              duration,
            }),
          );
        }
        statReachGoal('reels_seen', {
          reelId: reel.id,
          reelOwner: reel.ownerId,
          duration,
        });
      };
    }
  }, [isActive]);

  const handleErotic = useCallback(() => {
    statReachGoal('show_paywall_from_reels');
    router.navigate(SUBSCRIPTION_ADULT, {
      target: 'message',
      target_model: reel.ownerId,
    });
  }, [reel]);

  const handleEroticBanner = useCallback(() => {
    statReachGoal('show_paywall_from_reels_banner');
    router.navigate(SUBSCRIPTION_ADULT, {
      target: 'post',
      target_model: reel.ownerId,
    });
  }, [reel]);

  const handleSponsorBanner = useCallback(() => {
    statReachGoal('show_sponsor_paywall_from_reels_banner');
    router.navigate(SUBSCRIPTION_AUTHOR, {
      authorId: reel.ownerId,
    });
  }, [reel]);

  const handleMessage = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    // if (!decryptKeys[-1]) {
    //   return handleErotic();
    // }

    goTo(CHAT, { peerId: reel.ownerId });

    statReachGoal('reels_chat_open', {
      reelId: reel.id,
      reelOwner: reel.ownerId,
    });
  }, [reel, isLogged, handleErotic, decryptKeys]);

  const handleSendTips = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    dispatch(
      balanceActions.setTipsData({
        peerId: reel.ownerId,
        currency: CurrencyEnum.EUR,
      }),
    );
  }, [reel]);

  const handleProfileHandle = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    goTo(PROFILE, { id: reel.ownerId });
  }, [reel, isLogged]);

  const [isLikedAnimation, setIsLikedAnimation] = useState(false);
  const handleLike = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    const started = startViewAt.current;
    const duration = started > 0 ? Date.now() - started : 0;
    dispatch(
      reelsActions.toggleLike({
        ownerId: reel.ownerId,
        reelId: reel.id,
        isLiked: !reel.isLiked,
        duration,
        likesCount: reel.likes,
      }),
    );

    setIsLikedAnimation(true);

    if (!reel.isLiked) {
      statReachGoal('reels_like', {
        reelId: reel.id,
        reelOwner: reel.ownerId,
      });
    }
  }, [reel, startViewAt, isLogged]);

  const handleShare = useCallback(() => {
    const link: AttachLink = {
      type: 'reel',
      object: reel.id,
    };

    dispatch(
      mainActions.setPopup(
        <ShareSheet title="Share video" link={link} disableInternalShare />,
      ),
    );

    statReachGoal('reels_share', {
      reelId: reel.id,
      reelOwner: reel.ownerId,
    });
  }, [reel]);

  const handleMoreMenu = () => setOpenReelMenu(true);

  const handleDelete = useCallback(async () => {
    if (await confirmSheet(t('clips_delete'))) {
      dispatch(
        reelsActions.deleteReel({ reelId: reel.id, refreshReelsId: reel.ownerId }),
      );
      dispatch(mainActions.setPopup(null));
    }
  }, [reel]);

  const handleCloseReelMenu = useCallback(() => {
    setReported(false);
    setOpenReelMenu(false);
  }, []);

  const handleReport = useCallback(
    (reason: ReportReason) => {
      handleCloseReelMenu();
      return dispatch(reelsActions.reportReel({ reelId: reel.id, reason }));
    },
    [reel],
  );

  const handleRestore = useCallback(async () => {
    if (await confirmSheet('Are you sure you want to restore this reel?')) {
      dispatch(reelsActions.restoreReel(reel.id));
    }
  }, [reel]);

  const poster = useMemo(() => {
    if (reel.poster.indexOf('|') > -1) {
      const exp = reel.poster.split('|');
      return exp[1];
    } else {
      return reel.poster;
    }
  }, [reel.poster]);

  const handleOnChangeMoreMenu = (action: string) => {
    switch (action) {
      case 'unfollow':
        if (isFollowFromMe) {
          handleCloseReelMenu();
          return handleFollow();
        }
        break;
      case 'delete':
        handleCloseReelMenu();
        return handleDelete();
      case 'restore':
        handleCloseReelMenu();
        return handleRestore();
      case 'report':
        return setReported(true);
      case ReportReason.SPAM:
        return handleReport(ReportReason.SPAM);
      case ReportReason.FALSE_INFO:
        return handleReport(ReportReason.FALSE_INFO);
      case ReportReason.OFFENSIVE:
        return handleReport(ReportReason.OFFENSIVE);
    }
  };

  function renderSubscription() {
    if (!isPlay || !isLogged) {
      return null;
    }

    const totalContent = reel.videosCount + reel.photosCount;
    if (!totalContent) {
      return null;
    }

    if (!decryptKeys[-1]) {
      return (
        <div
          className={classNames({
            Reel__subscription: true,
            visible: isWatched,
          })}
        >
          <List>
            <ListItem
              description={t('reels_erotic_banner_caption', {
                content: reel.videosCount + reel.photosCount,
              })}
              chevron
              onClick={handleEroticBanner}
            >
              {t('reels_erotic_banner_title')}
            </ListItem>
          </List>
        </div>
      );
    } else if (!decryptKeys[reel.ownerId]) {
      return (
        <div
          className={classNames({
            Reel__subscription: true,
            visible: isWatched,
            sponsor: true,
          })}
        >
          <List>
            <ListItem
              description={t('reels_sponsor_banner_caption', {
                content: reel.videosCount + reel.photosCount,
              })}
              chevron
              onClick={handleSponsorBanner}
            >
              {t('reels_sponsor_banner_title')}
            </ListItem>
          </List>
        </div>
      );
    } else {
      return null;
    }
  }

  // const handleProgress = useCallback(() => {
  //   if (videoRef.current && videoRef.current.buffered.length > 0) {
  //     const buffered = videoRef.current.buffered.end(0);
  //     const duration = videoRef.current.duration;
  //     const progress = (buffered / duration) * 100;
  //     if (progress >= 80) {
  //       setLoading(false);
  //     }
  //   }
  // }, [videoRef, isActive]);

  const [followAnimation, setFollowAnimation] = useState(false);
  const [isVisibleFollowButton, setVisibleFollowButton] = useState(!isFollowFromMe);

  useEffect(() => {
    if (!isFollowFromMe) setVisibleFollowButton(true);
  }, [isFollowFromMe]);

  const handleFollow = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    setFollowAnimation(true);
    dispatch(
      isFollowFromMe
        ? profileActions.unfollow(reel.ownerId)
        : profileActions.follow({ id: reel.ownerId }),
    );
  }, [reel, isFollowFromMe]);

  return (
    <div className="Reel">
      {!isLoaded && (
        <div className="Reel__loader">
          <Loader />
        </div>
      )}
      <div
        className={classNames({
          Reel__video: true,
          poster: true,
          visible: isVisible && (isAnimating || !isSelected || !isLoaded),
        })}
        style={{ backgroundImage: `url(${poster})` }}
      />
      <div className="Reel__content">
        <div className="Reel__content__top" />
        <div className="Reel__footer__wrap">
          <div className="Reel__footer">
            {index === 0 && !reelsSwiped.current && (
              <div className="Reel__swipe_tip">{t('reel_swipe_tip')}</div>
            )}
            <div className="Reel__footer_user" onClick={handleProfileHandle}>
              <UnitPhoto unit={owner} size="extra-small" routeName={null} />
              <div className="Reel__footer_user__info">
                <div className="Reel__footer_user__info__name">
                  <UnitName unit={owner} />
                  <Verified objectId={reel.ownerId} />
                </div>
                <div className="Reel__footer_user__info__caption">
                  {t('followed_count', {
                    count: owner.followers,
                  })}
                  <div className="Reel__footer_user__info__caption_sep">·</div>
                  <div className="Reel__views">
                    <IcView20 />
                    {formatBigNumber(reel.views)}
                  </div>
                </div>
              </div>
            </div>
            {renderSubscription()}
          </div>

          <div className="Reel__actions">
            {!isCurrentUserReel && isVisibleFollowButton && !isFollowed && (
              <div className="Reel__action" onClick={handleFollow}>
                {isFollowFromMe ? (
                  <LottieIcon
                    name="follow"
                    size={28}
                    loop={false}
                    autoplay={followAnimation}
                    onComplete={() => setVisibleFollowButton(false)}
                  />
                ) : (
                  <LottieIcon
                    name="unfollow"
                    size={28}
                    loop={false}
                    autoplay={false}
                    initialSegment={[300, 300]}
                  />
                )}
                <div className="Reel__action__label">{t('reels_follow')}</div>
              </div>
            )}

            <div className="Reel__action" onClick={handleLike}>
              {reel.isLiked ? (
                <LottieIcon
                  name="storie_like"
                  size={28}
                  loop={false}
                  autoplay={isLikedAnimation}
                  onComplete={() => setIsLikedAnimation(false)}
                  initialSegment={!isLikedAnimation ? [180, 180] : undefined}
                />
              ) : (
                <IcLike24 />
              )}
              <div className="Reel__action__label">{reel.likes}</div>
            </div>
            <div className="Reel__action" onClick={handleMessage}>
              <IcMessage28 />
              <div className="Reel__action__label">{t('reels_write')}</div>
            </div>
            <div className="Reel__action" onClick={handleSendTips}>
              <IcDollar28 />
              <div className="Reel__action__label">{t('reels_tip')}</div>
            </div>
            <div className="Reel__action" onClick={handleShare}>
              <IcShare24 />
              <div className="Reel__action__label">{t('reels_share')}</div>
            </div>
            <div className="Reel__action" onClick={handleMoreMenu}>
              <IcMore24 />
            </div>
          </div>
        </div>
      </div>
      <ActionBottomSheet
        options={moreReelOptions}
        title={<UnitName unit={owner} />}
        onChange={handleOnChangeMoreMenu}
        onClose={handleCloseReelMenu}
        isOpened={isOpenReelMenu}
      />
    </div>
  );
};
